/**
 * react-i18next setup (TW-3434, architecture §6.1). Initializes the shared `@tc/i18n`
 * catalogues as the single in-memory resource bundle for the SPA. Importing this module
 * (side-effect, from `main.tsx`) registers the global i18next instance, so `useTranslation`
 * works everywhere — including the pre-login surfaces that sit outside <LanguageProvider>.
 *
 * The active language is owned by <LanguageProvider> (lib/i18n/language.tsx): it seeds from
 * localStorage (anonymous fallback) → `user.language` (authenticated default) and drives
 * `i18n.changeLanguage`. We seed the initial `lng` here from localStorage so a returning
 * visitor lands on the login screen in their last-used language before any session loads.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { defaultLocale, LOCALES, type Locale } from "@tc/i18n";
import en from "@tc/i18n/locales/en/common.json";
import es from "@tc/i18n/locales/es/common.json";

export const LANG_STORAGE_KEY = "tc.lang";

function storedLocale(): Locale | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.localStorage.getItem(LANG_STORAGE_KEY);
  return value != null && (LOCALES as readonly string[]).includes(value) ? (value as Locale) : undefined;
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { common: en },
    es: { common: es },
  },
  lng: storedLocale() ?? defaultLocale,
  fallbackLng: defaultLocale,
  ns: ["common"],
  defaultNS: "common",
  // React already escapes interpolated values, so i18next must not double-escape them.
  interpolation: { escapeValue: false },
  // Treat a missing key as a missing string (return the key), never `null`.
  returnNull: false,
});

export default i18n;
